import { Service, inject } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { DrupalService } from '@app/services/drupal.service';
import { FastlyChallengeService } from '@app/services/fastly-challenge.service';
import { Observable, of, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Service()
export class DrupalRouteGuardService {
  #drupal = inject(DrupalService);
  #challenge = inject(FastlyChallengeService);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.#activate(state);
  }

  #activate(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // Route data is keyed by pathname; keep query parameters in browser state.
    this.#drupal.setQueryParams(state.url);
    const plainUrl = state.url.split(/[?#]/, 1)[0] || '/';

    // Promise-based route loading cannot be cancelled by the router. The ID
    // lets the services discard a late result from a superseded navigation.
    const navigationId = this.#drupal.beginRouteNavigation();

    // The SSR route is already available before the first client render. Reuse
    // it and only hydrate shell data that remains in Angular's HTTP cache.
    if (this.#drupal.consumeInitialRoute(plainUrl)) {
      return from(this.#drupal.hydrateInitialRoute(plainUrl, navigationId)).pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error hydrating initial route:', err);
          return of(true);
        })
      );
    }

    this.#drupal.routeHandled.set(false);
    this.#drupal.route.set(null);

    // Challenge first: resolving would fetch the content before the gate runs.
    if (this.#drupal.challengeRequired()) {
      this.#drupal.challengeRequired.set(false);
      return from(this.#challenge.waitForEmbeddedChallenge()).pipe(
        switchMap((passed) => {
          // Keep the initial URL and empty SSR shell if the challenge fails.
          if (!passed) return of(true);
          return from(this.#drupal.resolveRoute(plainUrl, navigationId)).pipe(
            map(() => true)
          );
        }),
        catchError((err) => {
          console.error('Error gating bot-protected route:', err);
          this.#challenge.fail();
          return of(true);
        })
      );
    }

    return from(this.#drupal.resolveRoute(plainUrl, navigationId)).pipe(
      map(() => {
        return true;
      }),
      catchError((err) => {
        console.error('Error resolving route:', err);
        return of(true);
      })
    );
  }
}

export const drupalRouteGuard: CanActivateFn = (route, state) => {
  return inject(DrupalRouteGuardService).canActivate(route, state);
};
